import {
  Component,
  effect,
  ElementRef,
  HostListener,
  inject,
  input,
  model,
} from '@angular/core';

export interface SelectItem {
  value: string;
  label: string;
}

@Component({
  selector: 'app-select',
  templateUrl: './select.html',
})
export class Select {
  private elementRef = inject(ElementRef);
  id = input<string>('');
  name = input<string>('');
  items = input<SelectItem[]>([]);
  disabled = input<boolean>(false);
  selectedItem = model<SelectItem>();

  isOpen = false;
  highlightedIndex = -1;

  constructor() {
    effect(() => {
      const items = this.items();
      const currentSelected = this.selectedItem();

      // Auto-select first item if no selection and items exist
      if (items.length > 0 && !currentSelected) {
        this.selectedItem.set(items[0]);
      }
    });

    // React to external model changes and update highlighted index
    effect(() => {
      const selected = this.selectedItem();
      if (selected && this.isOpen) {
        const selectedIndex = this.items().findIndex(
          item => item.value === selected.value
        );
        this.highlightedIndex = selectedIndex >= 0 ? selectedIndex : -1;
      }
    });
  }

  toggleDropdown(): void {
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      // Initialize highlighted index to selected item or first item
      const selected = this.selectedItem();
      if (selected) {
        const selectedIndex = this.items().findIndex(
          item => item.value === selected.value
        );
        this.highlightedIndex = selectedIndex >= 0 ? selectedIndex : 0;
      } else {
        this.highlightedIndex = 0;
      }
    } else {
      this.highlightedIndex = -1;
    }
  }

  selectItem(item: SelectItem): void {
    this.selectedItem.set(item); // Use set() instead of emit()
    this.closeDropdown();
  }

  private closeDropdown(): void {
    this.isOpen = false;
    this.highlightedIndex = -1;
  }

  isSelected(item: SelectItem): boolean {
    return this.selectedItem()?.value === item.value;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.isOpen) return;

    const target = event.target as HTMLElement;
    if (!this.elementRef.nativeElement.contains(target)) {
      this.closeDropdown();
    }
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (!this.isOpen) {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        this.toggleDropdown();
      }
      return;
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.navigateDown();
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.navigateUp();
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        this.selectHighlightedItem();
        break;
      case 'Escape':
        event.preventDefault();
        this.closeDropdown();
        break;
    }
  }

  private navigateDown(): void {
    this.highlightedIndex = Math.min(
      this.highlightedIndex + 1,
      this.items.length - 1
    );
  }

  private navigateUp(): void {
    this.highlightedIndex = Math.max(this.highlightedIndex - 1, 0);
  }

  private selectHighlightedItem(): void {
    if (
      this.highlightedIndex >= 0 &&
      this.highlightedIndex < this.items().length
    ) {
      this.selectedItem.set(this.items()[this.highlightedIndex]);
      this.closeDropdown();
    }
  }
}
